import React, { useContext } from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import HandlingListContext from '../../../components/HandlingListContext';
import HandlingListItem from './HandlingListItem';
import HandlingListItemSkeleton from './HandlingListItemSkeleton';
import DataGrid from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import fetchMultipleProducts from '@afosto/utils-react/fetchMultipleProducts';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import { search as searchApi } from '@afosto/fulfilment/api/Search';
import {
  arrayLengthFormatter,
  productImageFormatter,
  productSkuFormatter,
  shipmentFormatter,
} from './formatters';

const DataListContainerGrid = props => <Grid container spacing={1} {...props} />;

const HandlingListItems = () => {
  const intl = useIntl();
  const { handlingList = {} } = useContext(HandlingListContext);

  const fetchItems = async dataSource =>
    dataSourceRequest(
      dataSource,
      async (data, requestOptions) =>
        searchApi.searchHandlingListItems(
          {
            constraints: [
              {
                key: 'list_ids',
                operator: 'in',
                value: [handlingList?.id],
              },
            ],
          },
          requestOptions,
        ),
      {
        transformData: async (data, source) => {
          const { pagination = {} } = source ?? {};
          const { itemsPerPage } = pagination;
          const skus = data.map(item => item.sku);
          const products = await fetchMultipleProducts(
            skus,
            {},
            {
              headers: {
                'x-page-size': itemsPerPage,
              },
            },
          );

          return data.reduce((acc, item) => {
            const [id] = item?.ids ?? [];
            const product = findObjectInArray(products, 'sku', item.sku);

            return [
              ...acc,
              {
                ...item,
                id,
                product,
              },
            ];
          }, []);
        },
      },
    );

  const dataSource = useDataGrid(fetchItems, {
    modules: ['dataProperties', 'pagination'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'image',
          name: '',
          type: 'productImage',
          width: '50px',
        },
        {
          key: 'sku',
          name: intl.formatMessage({
            id: 'dataGrid.columns.handlingListItems.sku',
            defaultMessage: 'SKU',
            description: 'The column name for column sku',
          }),
          type: 'productSku',
        },
        {
          key: 'description',
          name: intl.formatMessage({
            id: 'dataGrid.columns.handlingListItems.description',
            defaultMessage: 'Description',
            description: 'The column name for column description',
          }),
        },
        {
          key: 'position',
          name: intl.formatMessage({
            id: 'dataGrid.columns.handlingListItems.position',
            defaultMessage: 'Position',
            description: 'The column name for column position',
          }),
        },
        {
          key: 'quantity',
          name: intl.formatMessage({
            id: 'dataGrid.columns.handlingListItems.quantity',
            defaultMessage: 'Quantity',
            description: 'The column name for column quantity',
          }),
        },
        {
          key: 'handled_ids',
          name: intl.formatMessage({
            id: `dataGrid.columns.handlingListItems.${
              handlingList?.is_outbound ? 'picked' : 'placed'
            }`,
            defaultMessage: handlingList?.is_outbound ? 'Picked' : 'Placed',
            description: 'The column name for column mount handled',
          }),
          type: 'arrayLength',
        },
        {
          key: 'sorting_label',
          name: intl.formatMessage({
            id: 'dataGrid.columns.handlingListItems.crate',
            defaultMessage: 'Crate',
            description: 'The column name for column crate',
          }),
        },
        {
          key: 'shipment_id',
          name: intl.formatMessage({
            id: 'dataGrid.columns.handlingListItems.shipment',
            defaultMessage: 'Shipment',
            description: 'The column name for column shipment',
          }),
          type: 'shipment',
        },
      ],
    },
  });

  return (
    <>
      <Hidden implementation="js" mdUp>
        <DataList
          dataSource={dataSource}
          renderItem={item => (
            <HandlingListItem item={item} isOutbound={handlingList?.is_outbound} />
          )}
          renderSkeletonItem={key => <HandlingListItemSkeleton key={key} />}
          component={DataListContainerGrid}
        />
      </Hidden>
      <Hidden implementation="js" smDown>
        <DataGrid
          dataSource={dataSource}
          formatters={{
            productImage: productImageFormatter,
            productSku: productSkuFormatter,
            arrayLength: arrayLengthFormatter,
            shipment: shipmentFormatter,
          }}
        />
      </Hidden>
    </>
  );
};

export default HandlingListItems;
